import type { ReactNode } from 'react';
import {
  Ban,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  CalendarX,
  ChevronRight,
  Copy,
  Ellipsis,
  ExternalLink,
  Eye,
  Link2Off,
  MapPin,
  MessageSquare,
  Send,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { StatusPill, type PillTone } from '@/shared/ui/status-pill';
import { TableCell, TableRow } from '@/shared/ui/table';
import { cn } from '@/lib/utils';
import { DECISION_LABEL, formatDay, formatDayTime, STATE_LABEL, teethLabel } from './format';
import {
  availableActions,
  daysLeft,
  EXPIRY_WARNING_DAYS,
  isExpired,
  plural,
  relativeDay,
  rowState,
} from './logic';
import { useLinkNotes } from './queries';
import type { PatientLink, ReviewLink, ReviewLinkNote, RowState } from './types';

/**
 * The pill for each state. Mostly the shared vocabulary's own answer; the two
 * overrides are words it has no opinion on. "Awaiting review" is INFO (slate)
 * so it never looks like the amber "Changes requested", and "Closed" borrows
 * expired's red - the shared map has closed as positive, which is right for a
 * support issue and wrong for a link nobody answered.
 */
const PILL: Record<RowState, { status: string; tone?: PillTone }> = {
  awaiting: { status: 'awaiting', tone: 'info' },
  changes_requested: { status: 'changes_requested', tone: 'caution' },
  approved: { status: 'approved' },
  rejected: { status: 'rejected' },
  closed: { status: 'expired' },
};

/** Column count, for the expanded and empty rows' colSpan. */
export const COLUMN_COUNT = 9;

/* ── Cells ──────────────────────────────────────────────────────────────── */

function LinkLife({ link, now }: { link: ReviewLink; now: number }) {
  if (link.revokedAt) {
    return (
      <span className="text-[12.5px] text-muted-foreground">
        Revoked {formatDay(link.revokedAt)}
      </span>
    );
  }
  if (isExpired(link, now)) {
    return (
      <span className="text-[12.5px] text-muted-foreground">
        Expired {formatDay(link.expiresAt)}
      </span>
    );
  }
  const left = daysLeft(link, now);
  return (
    <span
      className={cn(
        'text-[12.5px]',
        left <= EXPIRY_WARNING_DAYS && 'font-medium text-pill-caution-foreground',
      )}
    >
      {plural(left, 'day', 'days')} left
    </span>
  );
}

function Opened({ link }: { link: ReviewLink }) {
  if (link.views === 0) return <span className="text-muted-foreground">Not yet</span>;
  return (
    <span className="flex items-center gap-1.5">
      <Eye className="size-3.5 text-muted-foreground" aria-hidden="true" />
      {link.views}×<span className="sr-only"> viewed</span>
      {link.notes > 0 && (
        <>
          <MessageSquare className="size-3.5 text-muted-foreground" aria-hidden="true" />
          {link.notes}
          <span className="sr-only">{link.notes === 1 ? ' note' : ' notes'}</span>
        </>
      )}
    </span>
  );
}

function patientLinkStatus(patientLink: PatientLink): {
  icon: ReactNode;
  label: string;
  className: string;
} {
  const icon = (Icon: typeof Smartphone) => <Icon className="size-3.5" aria-hidden="true" />;
  if (!patientLink.active) {
    return { icon: icon(Link2Off), label: 'Turned off', className: 'text-muted-foreground' };
  }
  switch (patientLink.appointment?.status) {
    case 'confirmed':
      return {
        icon: icon(CalendarCheck),
        label: 'Date confirmed',
        className: 'text-pill-positive-foreground',
      };
    case 'reschedule_requested':
      return {
        icon: icon(CalendarX),
        label: 'Wants another time',
        className: 'text-pill-caution-foreground',
      };
    case 'pending':
      return {
        icon: icon(CalendarClock),
        label: 'Awaiting patient',
        className: 'text-muted-foreground',
      };
    default:
      return { icon: icon(Smartphone), label: 'Open', className: '' };
  }
}

function PatientLinkStatus({ patientLink }: { patientLink: PatientLink | null }) {
  if (!patientLink) return <span className="text-[12px] text-muted-foreground">—</span>;
  const { icon, label, className } = patientLinkStatus(patientLink);
  return (
    <span className={cn('flex items-center gap-1.5 text-[12px] font-medium', className)}>
      {icon}
      {label}
    </span>
  );
}

/* ── ⋯ menu ─────────────────────────────────────────────────────────────── */

export interface RowActions {
  onExtend: (link: ReviewLink) => void;
  onResend: (link: ReviewLink) => void;
  /** Asks first - the caller owns the confirmation. */
  onRevoke: (link: ReviewLink) => void;
}

function copyLink(url: string) {
  navigator.clipboard.writeText(url).then(
    () => toast.success('Link copied.'),
    () => toast.error('Could not copy the link. Open it and copy it from the address bar.'),
  );
}

function ActionsMenu({
  link,
  now,
  actions,
}: {
  link: ReviewLink;
  now: number;
  actions: RowActions;
}) {
  const allowed = availableActions(link, now);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Link actions">
          <Ellipsis />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/*
          Shown disabled rather than hidden while there is no URL: the menu
          keeps its shape, and the reason is one hover away.
        */}
        {link.url ? (
          <>
            <DropdownMenuItem
              onSelect={() => {
                if (link.url) copyLink(link.url);
              }}
            >
              <Copy /> Copy link
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={link.url} target="_blank" rel="noreferrer">
                <ExternalLink /> Open as the dentist sees it
              </a>
            </DropdownMenuItem>
          </>
        ) : (
          <div title="The link is shown once, when it is sent - it cannot be shown again.">
            <DropdownMenuItem disabled>
              <Copy /> Copy link
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <ExternalLink /> Open as the dentist sees it
            </DropdownMenuItem>
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={!allowed.sendAgain}
          onSelect={() => {
            actions.onResend(link);
          }}
        >
          <Send /> Send to the dentist again
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!allowed.extend}
          onSelect={() => {
            actions.onExtend(link);
          }}
        >
          <CalendarPlus /> Extend by 30 days
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          disabled={!allowed.revoke}
          onSelect={() => {
            actions.onRevoke(link);
          }}
        >
          <Ban /> Revoke link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ── Expanded panel ─────────────────────────────────────────────────────── */

function PanelHeading({ children }: { children: ReactNode }) {
  return (
    <p className="mb-2 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
      {children}
    </p>
  );
}

function Details({ link, now, actions }: { link: ReviewLink; now: number; actions: RowActions }) {
  const { patientLink } = link;
  const canRevoke = availableActions(link, now).revoke;

  return (
    <div className="space-y-4">
      <div className="grid gap-6 md:grid-cols-3">
        <div>
          <PanelHeading>History</PanelHeading>
          <ol className="space-y-1.5 text-[12.5px]">
            <li>
              Sent by {link.sentBy} · {formatDayTime(link.sentAt)}
            </li>
            <li>
              {link.lastViewedAt
                ? `Last opened ${relativeDay(link.lastViewedAt, now)} · ${plural(link.views, 'view', 'views')}`
                : 'Not opened yet'}
            </li>
            {link.notes > 0 && <li>{plural(link.notes, 'note', 'notes')} from the dentist</li>}
            {link.decision !== 'awaiting' && link.decidedAt && (
              <li className="font-medium">
                {DECISION_LABEL[link.decision]} · {formatDayTime(link.decidedAt)}
              </li>
            )}
            {link.revokedAt && (
              <li className="font-medium">Revoked · {formatDayTime(link.revokedAt)}</li>
            )}
          </ol>
        </div>

        <div>
          <PanelHeading>Dentist&apos;s note</PanelHeading>
          {link.decisionNote ? (
            <p className="text-[12.5px] leading-relaxed">{link.decisionNote}</p>
          ) : (
            <p className="text-[12.5px] text-muted-foreground">
              {link.decision === 'awaiting' ? 'No decision yet.' : 'No note was left.'}
            </p>
          )}
        </div>

        <div>
          <PanelHeading>Patient link</PanelHeading>
          {patientLink ? (
            <div className="space-y-1 text-[12.5px]">
              <PatientLinkStatus patientLink={patientLink} />
              <p className="text-muted-foreground">
                Made by the dentist {formatDay(patientLink.createdAt)}
              </p>
              {patientLink.appointment && (
                <p>Fitting: {formatDayTime(patientLink.appointment.at)}</p>
              )}
              <p className="text-[11.5px] text-muted-foreground">
                The dentist manages this link from their review page.
              </p>
            </div>
          ) : (
            <p className="text-[12.5px] text-muted-foreground">
              The dentist has not shared it with the patient.
            </p>
          )}
        </div>
      </div>

      <Comments link={link} />

      {/*
        Out in the open as well as in the ⋯ menu: taking a link back is the
        action somebody comes to this panel looking for. Only while there is
        still something to revoke - an open, undecided link.
      */}
      {canRevoke && (
        <div className="flex justify-end border-t border-border pt-3">
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              actions.onRevoke(link);
            }}
          >
            <Ban /> Revoke link
          </Button>
        </div>
      )}
    </div>
  );
}

/** Pill colour for the decision a comment was sent with. */
const STAGE_TONE: Record<NonNullable<ReviewLinkNote['stage']>, PillTone> = {
  approve: 'positive',
  changes_requested: 'caution',
  reject: 'negative',
};

/**
 * The dentist's comments on the design, oldest first - the conversation the
 * decision came out of. Loaded when the row opens.
 */
function Comments({ link }: { link: ReviewLink }) {
  const notes = useLinkNotes(link.id, link.notes > 0);

  let body: ReactNode;
  if (link.notes === 0) {
    body = <p className="text-[12.5px] text-muted-foreground">No comments from the dentist yet.</p>;
  } else if (notes.isPending) {
    body = <p className="text-[12.5px] text-muted-foreground">Loading comments…</p>;
  } else if (notes.isError) {
    body = (
      <p className="text-[12.5px] text-destructive">
        Comments could not be loaded. {notes.error instanceof Error ? notes.error.message : ''}
      </p>
    );
  } else {
    body = (
      <ol className="space-y-2.5">
        {notes.data.map((note) => (
          <li key={note.id} className="rounded-lg border border-border bg-card px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-muted-foreground">
              <span className="font-semibold text-foreground">{note.authorName}</span>
              <span>{formatDayTime(note.createdAt)}</span>
              {note.pinned && (
                <span className="inline-flex items-center gap-1" title="Placed on the model">
                  <MapPin className="size-3" aria-hidden="true" /> On the model
                </span>
              )}
              {note.stage && (
                <StatusPill status={note.stage} tone={STAGE_TONE[note.stage]}>
                  {DECISION_LABEL[note.stage]}
                </StatusPill>
              )}
              {note.resolved && <StatusPill status="resolved">Resolved</StatusPill>}
            </div>
            <p className="mt-1 text-[12.5px] leading-relaxed whitespace-pre-line">{note.text}</p>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <div>
      <PanelHeading>Comments{link.notes > 0 ? ` (${String(link.notes)})` : ''}</PanelHeading>
      {body}
    </div>
  );
}

/* ── Row ────────────────────────────────────────────────────────────────── */

/**
 * Stops a click or keypress inside the actions cell reaching the row. React
 * bubbles synthetic events through portals, so this also covers the menu's
 * own items, which render outside the table.
 */
const keepToCell = {
  onClick: (event: React.SyntheticEvent) => {
    event.stopPropagation();
  },
  onKeyDown: (event: React.SyntheticEvent) => {
    event.stopPropagation();
  },
};

export function ReviewLinkRow({
  link,
  now,
  expanded,
  onToggle,
  actions,
}: {
  link: ReviewLink;
  now: number;
  expanded: boolean;
  onToggle: () => void;
  actions: RowActions;
}) {
  const state = rowState(link, now);
  const pill = PILL[state];

  return (
    <>
      <TableRow
        className="cursor-pointer"
        aria-expanded={expanded}
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <TableCell>
          <ChevronRight
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
            aria-hidden="true"
          />
        </TableCell>
        <TableCell>
          <div className="font-mono text-[12.5px] font-semibold">{link.patientRef}</div>
          <div className="text-[11.5px] text-muted-foreground">{teethLabel(link)}</div>
        </TableCell>
        <TableCell>
          <div className="text-[12.5px] font-medium">{link.dentist.name ?? '—'}</div>
          <div className="text-[11.5px] text-muted-foreground">
            {link.dentist.email ?? 'Link copied, not emailed'}
          </div>
        </TableCell>
        <TableCell className="text-[12.5px]">{formatDay(link.sentAt)}</TableCell>
        <TableCell>
          <LinkLife link={link} now={now} />
        </TableCell>
        <TableCell className="text-[12.5px]">
          <Opened link={link} />
        </TableCell>
        <TableCell>
          <StatusPill status={pill.status} tone={pill.tone} minWidth={118}>
            {STATE_LABEL[state]}
          </StatusPill>
        </TableCell>
        <TableCell>
          <PatientLinkStatus patientLink={link.patientLink} />
        </TableCell>
        <TableCell truncate={false} {...keepToCell}>
          <ActionsMenu link={link} now={now} actions={actions} />
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell truncate={false} />
          <TableCell truncate={false} colSpan={COLUMN_COUNT - 1} className="py-4 whitespace-normal">
            <Details link={link} now={now} actions={actions} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
